import {Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {SubSink} from '@Common/core/utils/subsink';
import {SnackBarToastyService, typeToast} from '@Common/widgets/general/snack-bar';
import {FileSizePipe} from '@Common/widgets/pipes/file-size';
import {AttachmentModel} from '@Common/widgets/general/attachments/interfaces/attachment.model';
import {AttachmentService} from '@Common/widgets/general/attachments/service/attachment.service';
import {Upload} from '@Common/widgets/general/attachments/interfaces/upload.interface';
import {ObjectGeneric} from '@Common/widgets/general/object-generic-django.interface';
import {FilesUtils} from '@Services/common';

/**
 * Composant de téléversement d'un fichier
 */
@Component({
  selector: 'p8-attachment-upload',
  templateUrl: './attachment-upload.component.html',
  styleUrls: ['./attachment-upload.component.scss']
})
export class AttachmentUploadComponent implements OnInit, OnDestroy {
  @Input()
  attachment: AttachmentModel;

  /**
   * Déclenchement du téléversement fichier
   */
  @Input()
  launchSignal: Subject<AttachmentModel>;

  @Input()
  coupled: boolean;

  @Output()
  onDestroy: EventEmitter<AttachmentModel> = new EventEmitter<AttachmentModel>();

  @Output()
  onFinished: EventEmitter<AttachmentModel> = new EventEmitter<AttachmentModel>();

  @Output()
  onProgress: EventEmitter<Upload> = new EventEmitter<Upload>();

  @Input()
  maxFileSize: number;

  state = '';
  warning = false;
  error = false;
  succes = false;

  unique: number;
  sent = false;
  loadingMask = 'loadingMask';
  loading = false;
  subscriptionSignal: Subscription;
  message: string;
  tooltip: string;

  warningColor = '#FFCC00';
  errorColor = 'red';
  succesColor = 'lightblue';

  objectToUpload: AttachmentModel;
  upload: Upload;
  subSink = new SubSink();

  constructor(@Inject(LOCALE_ID) private locale: string,
              public fileSvc: FilesUtils,
              private snackSvc: SnackBarToastyService,
              private attachmentService: AttachmentService) {
  }

  ngOnInit() {
    this.subscriptionSignal = this.launchSignal.subscribe(objectToLaunch => this.gotLaunchSignal(objectToLaunch));
    this.checkFileSize();
  }

  checkFileSize() {
    if (this.maxFileSize) {
      this.warning = this.maxFileSize < this.attachment.file.size;
    }

    if (this.warning) {
      this.state = this.warningColor;
      // tslint:disable-next-line:max-line-length
      this.tooltip = `Le fichier choisi est trop grand, il ne sera pas pris en compte. La taille maximale supportée est ${this.fileSvc.sizeFile(this.maxFileSize)} Mo.
      Le fichier que vous avez choisi a une taille de ${this.fileSvc.sizeFile(this.attachment.file.size, true)}`;
      this.snackSvc.toasty('Fichier', this.tooltip, typeToast.warning, 5500);
      this.destroyMe(); // TODO : voir avec le static false et la réinit du fichier à uploader qd erreur de taille
    }
  }
  /**
   * Téléversement fichier
   * @param {AttachmentModel} obj
   */
  gotLaunchSignal(obj: AttachmentModel): void {
    this.objectToUpload = obj;
    this.uploadFile();
  }

  destroyMe() {
    this.onDestroy.emit(this.attachment);
  }

  uploadFile() {
    if (this.sent) {
      return;
    }
    this.loading = true;
    this.attachment.object_id = this.objectToUpload.object_id;
    this.attachment.content_type = this.objectToUpload.content_type;
    this.attachment.application = this.objectToUpload.application;
    this.attachment.sub_dir = this.objectToUpload.sub_dir;
    this.attachment.filename_origin = this.objectToUpload.filename_origin;
    this.subSink.sink = this.attachmentService
      .upload(this.attachment)
      .subscribe((upload: Upload) => {
          this.upload = upload;
          this.onProgress.emit(upload);
          if (upload.state === 'DONE') {
            this.attachment.id = upload.id;
            this.loading = false;
            this.doSucces(this.attachment);
          }
        },
        error => {
          this.loading = false;
          this.doError(error);
        });
  }

  doSucces(attachment): void {
    this.sent = true;
    this.succes = true;
    this.state = this.succesColor;
    this.tooltip = 'Votre fichier est bien sauvegardé';
    this.onFinished.emit(attachment);
  }

  doError(error: any): void {
    this.error = false;
    this.state = this.errorColor;
    this.tooltip = error;
    this.onFinished.emit(null);
  }

  ngOnDestroy(): void {
    this.subscriptionSignal.unsubscribe();
    this.subSink.unsubscribe();
  }
}
